// Freeze's cold sequential ramp — lightness carries magnitude, per the design
// tokens (tokens.json#riskRamp). Hardcoded rather than computed: checked
// whether the hatch tint is a uniform white-mix of the fill and it isn't (0.37
// for the #b3cbe3 stop vs 0.69 for #f2f4f6), so no formula reproduces the three
// tints confirmed in the artboards. The top three stops (60/80/100) never
// appear hatched in the artboards — their hatch tints below are extrapolated
// and should be confirmed with design.
const FREEZE_RAMP = [
    { max: 0, fill: '#f2f4f6', hatch: '#fbfcfd' },
    { max: 20, fill: '#dbe6f0', hatch: '#eaf1f7' },
    { max: 40, fill: '#b3cbe3', hatch: '#cfdeed' },
    { max: 60, fill: '#7fa8d0', hatch: '#a9c5e0' }, // extrapolated
    { max: 80, fill: '#4372ab', hatch: '#7f9fc6' }, // extrapolated
    { max: 100, fill: '#1f3f7a', hatch: '#6076a0' }, // extrapolated
];

const STOP_MAXES = [0, 20, 40, 60, 80, 100];

const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const toHex = (rgb) => `#${rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;
const mix = (from, to, t) => from.map((v, i) => v + (to[i] - v) * t);

const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];

// Builds a ramp in one hue from a single base color: the four weak stops are
// tints toward white, the base itself lands on the 61-80 stop (the one the
// legend swatch shows, matching where freeze puts #4372ab), and the top stop is
// the base shaded toward black. So magnitude reads as strength of the SAME
// color rather than a different color per bar.
//
// Unlike the freeze table above, these hatch tints can be a formula, because
// nothing in the handoff constrains them: 0.45 toward white is the ratio that
// best reproduces freeze's own confirmed pair (#b3cbe3 -> #cfdeed).
function buildRamp(baseHex) {
    const base = hexToRgb(baseHex);
    const fills = [
        ...[0.08, 0.22, 0.42, 0.68].map((t) => mix(WHITE, base, t)),
        base,
        mix(base, BLACK, 0.28),
    ];
    return fills.map((rgb, i) => ({
        max: STOP_MAXES[i],
        fill: toHex(rgb),
        hatch: toHex(mix(rgb, WHITE, 0.45)),
    }));
}

// One ramp per risk indicator, so the chart reads as the variable it is showing.
// The arid base color is the one that same variable already carries in the
// Indicators chart (src/store/modules/variables/state.js, applied to the Plotly
// traces in DataAnalysis.vue#getChartData): arid -> arid "ARID" #F6721D.
// Freeze keeps the design-confirmed table verbatim. chill_hours has no such
// precedent in the Indicators chart (that tab colors chillH #4CAF50, but green
// reads as "good" for a risk-magnitude ramp, which is the wrong signal here) —
// a teal distinct from freeze's navy, reading as "cold" without colliding.
// The Drought Index keeps this single ramp in both </> directions: it encodes
// probability magnitude, not dryness, so it does not need to flip with the toggle.
const RAMPS = {
    freeze: FREEZE_RAMP,
    arid: buildRamp('#F6721D'),
    chill_hours: buildRamp('#00838F'),
    // The Danger color the Heat Index Risk Events chart already uses in the Indicators
    // tab (DataAnalysis.vue#getHIData).
    hi: buildRamp('#E53935'),
};

// Falls back to freeze rather than throwing: a catalog id this table does not
// know about should render in the original ramp, not crash the chart.
const rampFor = (indicator) => RAMPS[indicator] || FREEZE_RAMP;

// Bins on the ROUNDED integer percent, not the raw fraction — verified
// against every bar in the 2a-freeze-weekly mock: a raw 0.204 displays as
// "20%" and is colored with the ≤20 stop (#dbe6f0), not the ≤40 stop that
// binning on 20.4 would produce.
function rampStop(pctDisplay, indicator) {
    const ramp = rampFor(indicator);
    const pct = pctDisplay == null ? 0 : pctDisplay;
    return ramp.find((s) => pct <= s.max) || ramp[ramp.length - 1];
}

export function rampFill(pctDisplay, indicator) {
    return rampStop(pctDisplay, indicator).fill;
}

export function rampHatch(pctDisplay, indicator) {
    const s = rampStop(pctDisplay, indicator);
    return `repeating-linear-gradient(45deg, ${s.fill} 0 5px, ${s.hatch} 5px 10px)`;
}

// The indicator's own color, for the chart legend swatch — the 61-80 stop,
// which is the base color the ramp was built from.
export function rampAccent(indicator) {
    return rampFor(indicator)[4].fill;
}

export function inkOnFill(pctDisplay) {
    return pctDisplay != null && pctDisplay > 60 ? '#ffffff' : '#2c3540';
}

// Percentages round to whole numbers for display; under a small-sample
// warning they round to the nearest 10 instead. The unrounded value is
// used ONLY in the CSV export (riskCsv.js) — never in the UI.
export function displayPct(pctRaw, smallSample) {
    if (pctRaw == null) return null;
    const step = smallSample && smallSample.active ? (smallSample.precision || 10) : 1;
    return Math.round(pctRaw / step) * step;
}

export function formatPct(pctDisplay, smallSample) {
    if (pctDisplay == null) return '—';
    const prefix = smallSample && smallSample.active ? '~' : '';
    return `${prefix}${pctDisplay}%`;
}

export function formatDecimal(value, decimals) {
    if (value == null || Number.isNaN(value)) return '—';
    return Number(value).toFixed(decimals);
}

export function formatYearsOf(withEvent, total) {
    if (withEvent == null || total == null) return '—';
    return `${withEvent} of ${total}`;
}

// Appends the years behind a number in parentheses: "3 of 7 (2019, 2022, 2023)",
// "108.4 (2023)". Leaves the text untouched when there is no year to show, and never
// decorates a placeholder dash.
export function withYears(text, years) {
    const list = (Array.isArray(years) ? years : [years]).filter((y) => y != null);
    if (text === '—' || list.length === 0) return text;
    return `${text} (${list.join(', ')})`;
}

export function joinPeakLabels(peaks) {
    if (!peaks || peaks.length === 0) return null;
    const labels = peaks.map((p) => p.label);
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
